import { mapGetters } from 'vuex'
import { cloneDeep, debounce, isEqual } from 'lodash'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import {
  TABLE_COLUMNS_SETTING_KEY,
  resolveColumns,
  serializeColumns,
  mergeDormantColumns
} from '@/util/tableColumnPreferences'

/**
 * Column preference persistence for tables adopting the ColumnConfigurator
 * (VF-4175, epic VF-4136). The ColumnConfigurator itself is persistence-
 * agnostic (VF-4174); adopting tables (VF-4178-4181) mix this in, override
 * `tableColumnDefaults`, feed `resolvedTableColumns[tableId]` to the
 * configurator's `columns` prop, and bind `onColumnsUpdate` / `onColumnsReset`
 * to its events.
 *
 * Cross-tab sync rides the existing pipeline: UserSetting.afterSave() emits
 * `user-settings-updated`, UserModule refetches user/self (rate-limited to
 * one request per 500ms burst), and `resolvedTableColumns` recomputes from
 * the refreshed user record.
 */
export default {
  mixins: [CurrentUserMixin],
  data () {
    return {
      /**
       * tableId -> full resolved column array not yet confirmed by the store
       * (edits waiting on the debounced save and/or the user/self refresh).
       * Rendered with priority over the store so edits apply instantly and
       * never snap back while the round-trip completes.
       */
      pendingColumnChanges: {},

      /**
       * tableId -> true when a reset (entry deletion) is queued.
       */
      pendingColumnResets: {}
    }
  },
  computed: {
    ...mapGetters('featureModule', ['configurableTableColumnsEnabled']),

    /**
     * OVERRIDE IN THE ADOPTING COMPONENT (component computeds win over mixin
     * computeds). Shape: { [tableId]: { version: Number, columns: Array } }
     * where columns is the table's built-in default registry (VF-4174 shape).
     */
    tableColumnDefaults () {
      return {}
    },

    /**
     * The user's stored tableColumns object, type-checked. The API decodes
     * the JSON TEXT value, so corruption manifests as a wrong type here.
     *
     * @return {Object} {} when missing or malformed
     */
    storedTableColumns () {
      const value = this.user.getSetting(TABLE_COLUMNS_SETTING_KEY, {})
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return {}
      }
      return value
    },

    /**
     * { [tableId]: resolved ordered column registry }. Pure defaults when the
     * feature flag is off (VF-4173 contract). Pending local edits win over
     * the stored config until the store confirms them.
     *
     * @return {Object}
     */
    resolvedTableColumns () {
      const resolved = {}
      Object.keys(this.tableColumnDefaults).forEach(tableId => {
        const { version, columns } = this.tableColumnDefaults[tableId]
        if (!this.configurableTableColumnsEnabled) {
          resolved[tableId] = cloneDeep(columns)
          return
        }
        if (this.pendingColumnChanges[tableId]) {
          resolved[tableId] = this.pendingColumnChanges[tableId]
          return
        }
        const savedEntry = this.pendingColumnResets[tableId]
          ? undefined
          : this.storedTableColumns[tableId]
        resolved[tableId] = resolveColumns(columns, savedEntry, version).columns
      })
      return resolved
    }
  },
  watch: {
    /**
     * When the store user refreshes (own save round-trip or another tab's
     * change arriving via user-settings-updated), drop any pending overlay
     * the store now agrees with. This is what prevents a feedback loop: the
     * mixin only ever WRITES from onColumnsUpdate/onColumnsReset/mounted
     * convergence — never in reaction to a store change.
     */
    storedTableColumns: {
      deep: true,
      handler () {
        Object.keys(this.pendingColumnChanges).forEach(tableId => {
          const defaults = this.tableColumnDefaults[tableId]
          if (!defaults) {
            return
          }
          const storeResolved = resolveColumns(
            defaults.columns,
            this.storedTableColumns[tableId],
            defaults.version
          ).columns
          if (isEqual(storeResolved, this.pendingColumnChanges[tableId])) {
            this.$delete(this.pendingColumnChanges, tableId)
          }
        })
        Object.keys(this.pendingColumnResets).forEach(tableId => {
          if (this.storedTableColumns[tableId] === undefined) {
            this.$delete(this.pendingColumnResets, tableId)
          }
        })
      }
    },
    /**
     * The feature flag arrives asynchronously (VF-4173: fetched via /v2/feature
     * after boot), so a table can mount before it is set. mounted()'s
     * convergence is one-shot and would silently skip in that window; re-run it
     * when the flag flips on so stale saved configs still converge. resolveColumns
     * is idempotent, so a later mount with the flag already on is a no-op.
     */
    configurableTableColumnsEnabled (enabled) {
      if (enabled) {
        this.convergeTableColumns()
      }
    }
  },
  created () {
    /**
     * One debounced writer per component instance (500ms trailing edge):
     * a burst of toggles/reorders lands as a single update-settings request.
     * Instance-level (not module-level) so two mounted tables never share or
     * cancel each other's timer.
     */
    this.debouncedColumnSave = debounce(this.saveTableColumns, 500)
  },
  mounted () {
    this.convergeTableColumns()
  },
  beforeDestroy () {
    // VF-4199: closing the modal / navigating away before the debounce fires
    // must still persist the pending change.
    this.flushTableColumnSaves()
  },
  methods: {
    /**
     * VF-4201: converge stale saved configs (version mismatch, pruned or new
     * columns) by writing the merged result back once. resolveColumns is
     * idempotent, so the converged entry resolves with changed=false on the
     * next run — no repeat writes, no loop. No-op when the flag is off; called
     * from mounted() and re-run when the flag flips on (async arrival, VF-4173).
     */
    convergeTableColumns () {
      if (!this.configurableTableColumnsEnabled) {
        return
      }
      Object.keys(this.tableColumnDefaults).forEach(tableId => {
        const { version, columns } = this.tableColumnDefaults[tableId]
        const savedEntry = this.storedTableColumns[tableId]
        if (savedEntry === undefined) {
          return
        }
        const { columns: resolved, changed } = resolveColumns(columns, savedEntry, version)
        if (changed) {
          this.$set(this.pendingColumnChanges, tableId, resolved)
          this.debouncedColumnSave()
        }
      })
    },
    /**
     * Bind to the ColumnConfigurator's `update:columns` event (VF-4174).
     * Applies the new registry immediately (via resolvedTableColumns) and
     * queues the debounced save.
     *
     * @param {Array} columns full ordered registry emitted by the configurator
     * @param {String} tableId
     */
    onColumnsUpdate (columns, tableId) {
      if (!this.configurableTableColumnsEnabled || !this.tableColumnDefaults[tableId]) {
        return
      }
      if (this.pendingColumnResets[tableId]) {
        const { version, columns: defaults } = this.tableColumnDefaults[tableId]
        if (isEqual(cloneDeep(columns), resolveColumns(defaults, undefined, version).columns)) {
          return // the configurator echoing defaults after reset — keep the deletion
        }
      }
      this.$delete(this.pendingColumnResets, tableId)
      this.$set(this.pendingColumnChanges, tableId, cloneDeep(columns))
      this.debouncedColumnSave()
    },

    /**
     * Bind to the ColumnConfigurator's `reset` event (VF-4174). Deletes the
     * table's entry from the stored object — clearing, not overwriting with
     * defaults, so future default changes flow through. The configurator
     * emits update:columns with the defaults right after `reset`; the
     * pending-reset guard in onColumnsUpdate keeps that echo from
     * re-persisting an explicit defaults entry.
     *
     * @param {String} tableId
     */
    onColumnsReset (tableId) {
      if (!this.configurableTableColumnsEnabled || !this.tableColumnDefaults[tableId]) {
        return
      }
      this.$delete(this.pendingColumnChanges, tableId)
      this.$set(this.pendingColumnResets, tableId, true)
      this.debouncedColumnSave()
    },

    /**
     * Force any pending debounced write to run now. Called automatically on
     * destroy; adopting tables normally never call it directly.
     */
    flushTableColumnSaves () {
      if (this.debouncedColumnSave) {
        this.debouncedColumnSave.flush()
      }
    },

    /**
     * The actual write (only ever invoked through the debounced wrapper or
     * its flush). Read-modify-write of the whole tableColumns object: start
     * from the store's current value, apply this instance's pending resets
     * and changes, POST the result. Sibling tables' entries ride through
     * untouched. Pending state is kept until the store confirms it (see the
     * storedTableColumns watcher) so the UI never snaps back mid-round-trip.
     *
     * @return {Promise|undefined}
     */
    saveTableColumns () {
      const pendingChangeIds = Object.keys(this.pendingColumnChanges)
      const pendingResetIds = Object.keys(this.pendingColumnResets)
      if (pendingChangeIds.length === 0 && pendingResetIds.length === 0) {
        return
      }

      const tableColumns = cloneDeep(this.storedTableColumns)
      pendingResetIds.forEach(tableId => {
        delete tableColumns[tableId]
      })
      pendingChangeIds.forEach(tableId => {
        const { version, columns: defaults } = this.tableColumnDefaults[tableId]
        // VF-4416: the pending overlay holds renderable columns only (the
        // configurator never sees dormant entries), so the stored entry's
        // dormant set must be merged back in or this write would drop it.
        // resolveColumns already handles the cases where dormant entries must
        // NOT survive: a version bump or a cap overflow returns them pruned.
        const { dormant } = resolveColumns(defaults, this.storedTableColumns[tableId], version)
        tableColumns[tableId] = {
          version,
          columns: mergeDormantColumns(
            serializeColumns(this.pendingColumnChanges[tableId]),
            dormant
          )
        }
      })

      return this.user.updateSettings({ [TABLE_COLUMNS_SETTING_KEY]: tableColumns })
        .catch(() => {
          // Leave pending state in place: the UI keeps the user's choice and
          // the next edit retries the write. No banner — column prefs are
          // not worth interrupting the user's flow for.
        })
    }
  }
}
